// rraFreezeActions — the AppFolio tasks on a case's freeze ledger, in the console's words.
//
// Billing changes happen in AppFolio by hand, so each task is raised once, emailed to the billing
// team, and marked applied by staff. v1 raised one kind (suspend the animal charges on a tenancy
// case). RRA v2.2 part B adds the decision tasks: start charges (a denial on a lease that carries the
// pending-decision clause), keep them off (a tenancy animal request approved) and resume them (a
// tenancy animal request denied). Every kind shares one flow: "Mark applied in AppFolio".
//
//   cd apps/web && npx tsx --test src/lib/rraFreezeActions.test.ts
//
// Type-only imports of the client. Neutral operational wording — never disability detail, never an id.

import type { RraFreezeActionView } from './rraStaffClient';

export type RraFreezeActionKind = RraFreezeActionView['kind'];

/** The label under which a task is listed. */
export const FREEZE_ACTION_LABEL: Record<RraFreezeActionKind, string> = {
  suspend_animal_charges: 'Suspend animal charges',
  start_animal_charges: 'Start animal charges',
  keep_animal_charges_off: 'Keep animal charges off',
  resume_animal_charges: 'Resume animal charges',
};

/** The label for a task; a kind this build does not know reads as a plain AppFolio task rather than as its code. */
export function freezeActionLabel(kind: string): string {
  return (FREEZE_ACTION_LABEL as Record<string, string>)[kind] ?? 'AppFolio task';
}

/** The one line of detail the API projects ("Keep animal charges off for Rex"), trimmed — or null when there is none. */
export function freezeActionSummary(action: Pick<RraFreezeActionView, 'summary'>): string | null {
  const s = action.summary?.trim();
  return s ? s : null;
}

/** What "Mark applied" confirms, in the sentence above the list. The same flow for every kind. */
export const FREEZE_TASKS_INTRO =
  'Charge changes happen in AppFolio by hand. Each task below was emailed to the billing team; mark it once the change is actually applied. A task pending more than a day raises the needs-attention flag.';
